/**
 * useBenchmarkCTA — drives the A/B test on the "Benchmark My Org" CTA.
 *
 * - Persists a 50/50 variant assignment in localStorage so the same visitor
 *   always sees the same copy (no thrashing between page loads).
 * - Fires a de-duped impression beacon on mount and a click beacon on
 *   navigate — both keyed by a stable session UUID.
 * - Returns everything the CTA needs: the label, the attributed href
 *   (with utm_variant + utm_source), and a click handler.
 */
import { useEffect, useMemo } from 'react';

const VARIANT_KEY = 'kstreak_cta_variant';
const SESSION_KEY = 'kstreak_cta_session';

const LABELS = {
  a: 'Benchmark My Org',
  b: 'Get Your Free KM Score',
};

function ensureVariant() {
  try {
    let v = localStorage.getItem(VARIANT_KEY);
    if (v !== 'a' && v !== 'b') {
      v = Math.random() < 0.5 ? 'a' : 'b';
      localStorage.setItem(VARIANT_KEY, v);
    }
    return v;
  } catch {
    return 'a';
  }
}

function ensureSessionId() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = (crypto?.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return `s_${Date.now()}`;
  }
}

async function fireEvent({ variant, source, type, sessionId }) {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/km-benchmarking/atlas/cta/event`;
    // Fire-and-forget; keepalive so clicks that navigate away still send.
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variant, source, type, session_id: sessionId }),
      keepalive: true,
    });
  } catch {
    /* beacon is best-effort */
  }
}

export function useBenchmarkCTA(source) {
  const variant = useMemo(ensureVariant, []);
  const sessionId = useMemo(ensureSessionId, []);

  // Fire impression once per mount. Backend dedupes per (session, source, type).
  useEffect(() => {
    fireEvent({ variant, source, type: 'impression', sessionId });
  }, [variant, sessionId, source]);

  const href = `/atlas?utm_variant=${variant}&utm_source=${encodeURIComponent(source)}`;

  const onClick = () => {
    fireEvent({ variant, source, type: 'click', sessionId });
  };

  return {
    variant,
    label: LABELS[variant],
    href,
    onClick,
    sessionId,
  };
}
